import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import {
  FaIdCard, FaFingerprint, FaLock, FaCheckCircle, FaCheck,
  FaArrowRight, FaBolt, FaFileAlt, FaRupeeSign, FaShieldAlt
} from 'react-icons/fa';
import './apply-new.css';

const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const ApplyNew = () => {
  const [panNumber, setPanNumber] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const [loanDetails, setLoanDetails] = useState({
    firstName: '',
    lastName: '',
    monthlyIncome: '',
    loanAmount: '',
    dob: '',
    gender: '',
    mobile: '',
    alternateMobile: '',
    email: '',
    officialEmail: '',
    obligations: '',
    state: '',
    city: '',
    pincode: '',
  });

  const formatAadhaar = (digits) =>
    digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();

  const handlePanChange = (event) => {
    const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setPanNumber(value);
    setShowOtp(false);
    setOtpVerified(false);
    setOtp('');
    setErrors((current) => ({ ...current, panNumber: '', otp: '' }));
  };

  const handleAadhaarChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 12);
    setAadhaarNumber(value);
    setShowOtp(false);
    setOtpVerified(false);
    setOtp('');
    setErrors((current) => ({ ...current, aadhaarNumber: '', otp: '' }));
  };

  const handleSendOtp = () => {
    const nextErrors = {};

    if (!PAN_PATTERN.test(panNumber)) {
      nextErrors.panNumber = 'Enter a valid 10-character PAN number';
    }

    if (aadhaarNumber.length !== 12) {
      nextErrors.aadhaarNumber = 'Enter a valid 12-digit Aadhaar number';
    }

    setErrors(nextErrors);
    setShowOtp(Object.keys(nextErrors).length === 0);
  };

  const handleVerifyOtp = () => {
    if (otp !== '123456') {
      setErrors((current) => ({ ...current, otp: 'Invalid OTP. Use 123456 for testing.' }));
      return;
    }
    setErrors((current) => ({ ...current, otp: '' }));
    setOtpVerified(true);
  };

  const handleLoanDetailChange = (event) => {
    const { name, value } = event.target;
    setLoanDetails((current) => ({ ...current, [name]: value }));
  };

  return (
    <main className="apn-page">
      <section className={`apn-card ${otpVerified ? 'apn-card-details' : ''}`}>

        {/* ---------- Left brand panel ---------- */}
        <aside className="apn-left">
          <span className="apn-left-circle apn-left-circle-1" />
          <span className="apn-left-circle apn-left-circle-2" />
          <span className="apn-left-circle apn-left-circle-3" />

          <div className="apn-left-content">
            <span className="apn-left-badge"><FaBolt /> Quick Loan</span>

            <h2>Get funds in your account within minutes</h2>
            <p>Complete a quick identity check and unlock your personalised loan offer.</p>

            <ul className="apn-left-list">
              <li>
                <span className="apn-left-tick"><FaCheck /></span>
                <div>
                  <strong>Instant Approval</strong>
                  <small>Real-time PAN &amp; Aadhaar verification</small>
                </div>
              </li>
              <li>
                <span className="apn-left-tick"><FaCheck /></span>
                <div>
                  <strong>100% Paperless</strong>
                  <small>No branch visit, no documents</small>
                </div>
              </li>
              <li>
                <span className="apn-left-tick"><FaCheck /></span>
                <div>
                  <strong>Safe &amp; Secure</strong>
                  <small>Bank-grade 256-bit encryption</small>
                </div>
              </li>
            </ul>

            <div className="apn-left-bottom">
              <FaShieldAlt />
              <span>Trusted by <strong>50,000+</strong> happy customers</span>
            </div>
          </div>
        </aside>

        {/* ---------- Right form panel ---------- */}
        <div className="apn-right">

          <div className="apn-steps">
            <div className={`apn-step ${otpVerified ? 'done' : 'active'}`}>
              <span className="apn-step-num">{otpVerified ? <FaCheck /> : '1'}</span>
              <div className="apn-step-meta">
                <small>Step 1</small>
                <strong>Verification</strong>
              </div>
            </div>
            <span className={`apn-step-track ${otpVerified ? 'filled' : ''}`} />
            <div className={`apn-step ${otpVerified ? 'active' : ''}`}>
              <span className="apn-step-num">2</span>
              <div className="apn-step-meta">
                <small>Step 2</small>
                <strong>Loan Details</strong>
              </div>
            </div>
          </div>

          <h1 className="apn-title">{otpVerified ? 'Complete your application' : 'Verify your identity'}</h1>
          <p className="apn-subtitle">
            {otpVerified
              ? 'Review the auto-filled details and complete the remaining fields'
              : 'Enter your PAN & Aadhaar — takes less than 2 minutes'}
          </p>

          <Form className="apn-form" noValidate>

            <Form.Group className="apn-field" controlId="newPanNumber">
              <Form.Label>PAN Number <em>*</em></Form.Label>
              <div className={`apn-input ${errors.panNumber ? 'has-error' : ''} ${PAN_PATTERN.test(panNumber) ? 'is-done' : ''}`}>
                <span className="apn-input-icon"><FaIdCard /></span>
                <Form.Control
                  type="text"
                  inputMode="text"
                  maxLength={10}
                  value={panNumber}
                  onChange={handlePanChange}
                  placeholder="ABCDE1234F"
                  autoComplete="off"
                />
                {PAN_PATTERN.test(panNumber) && <FaCheckCircle className="apn-input-done" />}
              </div>
              {errors.panNumber && <div className="apn-error">{errors.panNumber}</div>}
            </Form.Group>

            <Form.Group className="apn-field" controlId="newAadhaarNumber">
              <Form.Label>Aadhaar Number <em>*</em></Form.Label>
              <div className={`apn-input ${errors.aadhaarNumber ? 'has-error' : ''} ${aadhaarNumber.length === 12 ? 'is-done' : ''}`}>
                <span className="apn-input-icon"><FaFingerprint /></span>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={14}
                  value={formatAadhaar(aadhaarNumber)}
                  onChange={handleAadhaarChange}
                  placeholder="0000 0000 0000"
                  autoComplete="off"
                />
                {aadhaarNumber.length === 12 && <FaCheckCircle className="apn-input-done" />}
              </div>
              {errors.aadhaarNumber && <div className="apn-error">{errors.aadhaarNumber}</div>}
            </Form.Group>

            {!showOtp && (
              <Button type="button" className="apn-btn" onClick={handleSendOtp}>
                Send OTP <FaArrowRight />
              </Button>
            )}

            {showOtp && !otpVerified && (
              <div className="apn-otp">
                <div className="apn-otp-note">
                  <FaShieldAlt />
                  Test OTP: <strong>&nbsp;123456</strong>
                </div>

                <Form.Group className="apn-field" controlId="newAadhaarOtp">
                  <Form.Label>Enter OTP <em>*</em></Form.Label>
                  <div className={`apn-input ${errors.otp ? 'has-error' : ''}`}>
                    <span className="apn-input-icon"><FaLock /></span>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={(event) => {
                        setOtp(event.target.value.replace(/\D/g, ''));
                        setErrors((current) => ({ ...current, otp: '' }));
                      }}
                      placeholder="6-digit OTP"
                      autoComplete="one-time-code"
                      className="apn-otp-box"
                    />
                  </div>
                  {errors.otp && <div className="apn-error">{errors.otp}</div>}
                </Form.Group>

                <Button type="button" className="apn-btn" onClick={handleVerifyOtp}>
                  Verify &amp; Continue <FaArrowRight />
                </Button>

                <button type="button" className="apn-resend" onClick={handleSendOtp}>
                  Didn&apos;t receive OTP? <span>Resend</span>
                </button>
              </div>
            )}

            {otpVerified && (
              <>
                <div className="apn-success apn-success-inline">
                  <span className="apn-success-icon"><FaCheckCircle /></span>
                  <div>
                    <strong>Identity Verified!</strong>
                    <p>Your PAN &amp; Aadhaar have been verified successfully.</p>
                  </div>
                </div>

                <div className="apn-details-heading">
                  <div>
                    <h2>Personal &amp; Loan Details</h2>
                    <p>Fields marked with * are mandatory</p>
                  </div>
                  <span className="apn-autofill-key"><FaCheckCircle /> Auto-filled</span>
                </div>

                <div className="apn-details-grid">
                  <Form.Group className="apn-detail-field" controlId="firstName">
                    <Form.Label>First Name <em>*</em> <small>Auto-filled</small></Form.Label>
                    <Form.Control name="firstName" value={loanDetails.firstName} onChange={handleLoanDetailChange} placeholder="First name" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="lastName">
                    <Form.Label>Last Name <small>Auto-filled</small></Form.Label>
                    <Form.Control name="lastName" value={loanDetails.lastName} onChange={handleLoanDetailChange} placeholder="Last name" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="monthlyIncome">
                    <Form.Label>Monthly Income <em>*</em></Form.Label>
                    <Form.Control type="number" name="monthlyIncome" value={loanDetails.monthlyIncome} onChange={handleLoanDetailChange} placeholder="Enter monthly income" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="loanAmount">
                    <Form.Label>Loan Amount <em>*</em></Form.Label>
                    <Form.Control type="number" name="loanAmount" value={loanDetails.loanAmount} onChange={handleLoanDetailChange} placeholder="Enter loan amount" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="dob">
                    <Form.Label>Date of Birth <em>*</em> <small>Auto-filled</small></Form.Label>
                    <Form.Control type="date" name="dob" value={loanDetails.dob} onChange={handleLoanDetailChange} />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="gender">
                    <Form.Label>Gender <em>*</em> <small>Auto-filled</small></Form.Label>
                    <Form.Select name="gender" value={loanDetails.gender} onChange={handleLoanDetailChange}>
                      <option value="">Select gender</option>
                      <option value="MALE">Male</option>
                      <option value="FEMALE">Female</option>
                      <option value="OTHER">Other</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="mobile">
                    <Form.Label>Mobile <em>*</em></Form.Label>
                    <Form.Control type="tel" inputMode="numeric" maxLength={10} name="mobile" value={loanDetails.mobile} onChange={handleLoanDetailChange} placeholder="10-digit mobile number" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="alternateMobile">
                    <Form.Label>Alternate Mobile <em>*</em></Form.Label>
                    <Form.Control type="tel" inputMode="numeric" maxLength={10} name="alternateMobile" value={loanDetails.alternateMobile} onChange={handleLoanDetailChange} placeholder="Alternate mobile number" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="email">
                    <Form.Label>Email <em>*</em></Form.Label>
                    <Form.Control type="email" name="email" value={loanDetails.email} onChange={handleLoanDetailChange} placeholder="Personal email address" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="officialEmail">
                    <Form.Label>Official Email</Form.Label>
                    <Form.Control type="email" name="officialEmail" value={loanDetails.officialEmail} onChange={handleLoanDetailChange} placeholder="Official email address" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="obligations">
                    <Form.Label>Obligations</Form.Label>
                    <Form.Control type="number" name="obligations" value={loanDetails.obligations} onChange={handleLoanDetailChange} placeholder="Existing monthly obligations" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="state">
                    <Form.Label>State <em>*</em> <small>Auto-filled</small></Form.Label>
                    <Form.Control name="state" value={loanDetails.state} onChange={handleLoanDetailChange} placeholder="State" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="city">
                    <Form.Label>City <small>Auto-filled</small></Form.Label>
                    <Form.Control name="city" value={loanDetails.city} onChange={handleLoanDetailChange} placeholder="City" />
                  </Form.Group>

                  <Form.Group className="apn-detail-field" controlId="pincode">
                    <Form.Label>Pincode <small>Auto-filled</small></Form.Label>
                    <Form.Control type="text" inputMode="numeric" maxLength={6} name="pincode" value={loanDetails.pincode} onChange={handleLoanDetailChange} placeholder="6-digit pincode" />
                  </Form.Group>
                </div>

                <Button type="button" className="apn-btn apn-details-submit">
                  Continue <FaArrowRight />
                </Button>
              </>
            )}
          </Form>

          <div className="apn-secure">
            <span><FaLock /> 256-bit encryption</span>
            <span><FaFileAlt /> No paperwork</span>
            <span><FaRupeeSign /> Zero hidden charges</span>
          </div>
        </div>

      </section>
    </main>
  );
};

export default ApplyNew;
